/**
 * Subscription Status Service
 *
 * Provides subscription status checks using the new subscription API architecture.
 * Replaces the old Cognito attribute-based checks in userStatusService.js
 */

import { getSubscriptionStatus } from '../api/subscriptionService';
import { isTenantContractBased } from '../tenant/tenantService';

/**
 * Check if user has an active subscription
 * @param {object} cognitoUser - Cognito user object (for tenant check)
 * @returns {Promise<boolean>} True if subscription is active
 */
export async function isSubscriptionActive(cognitoUser) {
  try {
    // Check if tenant uses contract-based payment (bypasses subscription checks)
    const isContract = isTenantContractBased(cognitoUser);
    if (isContract) {
      return true;
    }

    // Get subscription from API
    const subscription = await getSubscriptionStatus();
    if (!subscription) {
      return false;
    }

    // Check if subscription is active and not cancelled
    return subscription.status === 'active' && !subscription.cancel_at_period_end;
  } catch (error) {
    console.error('[subscriptionStatusService] Error checking subscription status:', error);
    return false;
  }
}

/**
 * Check if subscription is active but will be cancelled at period end
 * @returns {Promise<boolean>} True if subscription will cancel
 */
export async function isSubscriptionCancelling() {
  try {
    const subscription = await getSubscriptionStatus();
    if (!subscription) {
      return false;
    }

    return subscription.status === 'active' && subscription.cancel_at_period_end === true;
  } catch (error) {
    console.error('[subscriptionStatusService] Error checking cancellation status:', error);
    return false;
  }
}

/**
 * Check if subscription exists but is not active
 * @returns {Promise<boolean>} True if subscription exists but inactive
 */
export async function isSubscriptionInactive() {
  try {
    const subscription = await getSubscriptionStatus();
    if (!subscription) {
      return false;
    }

    return subscription.status !== 'active';
  } catch (error) {
    console.error('[subscriptionStatusService] Error checking inactive status:', error);
    return false;
  }
}

/**
 * Check if subscription has expired
 * @returns {Promise<boolean>} True if subscription has expired
 */
export async function isSubscriptionExpired() {
  try {
    const subscription = await getSubscriptionStatus();
    if (!subscription) {
      return true; // No subscription = expired
    }

    // Check if current period has ended
    const now = new Date();
    const periodEnd = new Date(subscription.current_period_end);

    return periodEnd < now;
  } catch (error) {
    console.error('[subscriptionStatusService] Error checking expiry:', error);
    return true;
  }
}

/**
 * Get full subscription details
 * @returns {Promise<object|null>} Subscription object or null
 */
export async function getSubscription() {
  try {
    return await getSubscriptionStatus();
  } catch (error) {
    console.error('[subscriptionStatusService] Error getting subscription:', error);
    return null;
  }
}

/**
 * Check if user is eligible to subscribe
 * User is eligible if:
 * - They are signed in
 * - They don't have an active subscription
 * - Their tenant is not contract-based
 *
 * @param {object} cognitoUser - Cognito user object
 * @returns {Promise<boolean>} True if eligible to subscribe
 */
export async function isEligibleToSubscribe(cognitoUser) {
  try {
    // Must be signed in
    if (!cognitoUser || !cognitoUser.username) {
      return false;
    }

    // Contract-based tenants don't need subscriptions
    const isContract = isTenantContractBased(cognitoUser);
    if (isContract) {
      return false;
    }

    // Check if already has active subscription
    const hasActiveSubscription = await isSubscriptionActive(cognitoUser);

    return !hasActiveSubscription;
  } catch (error) {
    console.error('[subscriptionStatusService] Error checking eligibility:', error);
    return false;
  }
}

/**
 * Get subscription status summary for UI display
 * @returns {Promise<object>} Status summary
 */
export async function getSubscriptionStatusSummary() {
  try {
    const subscription = await getSubscriptionStatus();

    if (!subscription) {
      return {
        hasSubscription: false,
        isActive: false,
        isCancelling: false,
        status: null,
        periodEnd: null
      };
    }

    return {
      hasSubscription: true,
      isActive: subscription.status === 'active' && !subscription.cancel_at_period_end,
      isCancelling: subscription.status === 'active' && subscription.cancel_at_period_end,
      status: subscription.status,
      periodEnd: subscription.current_period_end,
      cancelAtPeriodEnd: subscription.cancel_at_period_end
    };
  } catch (error) {
    console.error('[subscriptionStatusService] Error getting status summary:', error);
    return {
      hasSubscription: false,
      isActive: false,
      isCancelling: false,
      status: null,
      periodEnd: null
    };
  }
}
